import { useMemo, useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackCollectionCover } from "@/lib/catalog/fallback-images";
import { corporatePackQuery } from "@/lib/corporate/queries";
import { requestQuoteFn } from "@/lib/corporate/corporate.functions";
import { formatPaise } from "@/lib/corporate/format";
import { priceTiered, tierFromRow, validateOrderQty } from "@/lib/corporate/tiers";
import { isValidGst } from "@/lib/corporate/types";
import { canonicalUrl } from "@/lib/seo/site";

export const Route = createFileRoute("/corporate/$slug")({
  head: ({ params }) => {
    const name = params.slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const url = canonicalUrl(`/corporate/${params.slug}`);
    return {
      meta: [
        { title: `${name} — Corporate Bulk Order | CB16 Atelier` },
        { property: "og:url", content: url },
        { name: "description", content: `Bulk pricing, minimum order quantity, branding options and GST invoicing for ${name}.` },
        { property: "og:title", content: `${name} — Corporate Bulk Order | CB16 Atelier` },
        { property: "og:description", content: `Request a corporate quote for ${name} with tiered volume pricing.` },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  loader: async ({ context, params }) => {
    const res = await context.queryClient.ensureQueryData(corporatePackQuery(params.slug));
    if (!res.context) throw notFound();
  },
  component: CorporatePackPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>Pack not available</h1>
      <p className="mt-3 text-sm text-black/60">This corporate pack is no longer published.</p>
      <Link to="/corporate" className="mt-6 inline-block text-[11px] uppercase tracking-[0.22em] text-[#C6A15B]">Back to corporate</Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>Pack unavailable</h1>
    </div>
  ),
});

const FIELD = "w-full border border-black/15 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#C6A15B]";
const LABEL = "block text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50";

function CorporatePackPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(corporatePackQuery(slug));
  const ctx = data.context!;
  const { pack, options, tiers } = ctx;

  const [qty, setQty] = useState(options.minOrderQty);
  const [form, setForm] = useState({
    companyName: "",
    gstNumber: "",
    poNumber: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const submit = useServerFn(requestQuoteFn);

  const tierLikes = useMemo(() => tiers.map(tierFromRow), [tiers]);
  const priced = useMemo(
    () => priceTiered(pack.basePriceCents, qty, tierLikes),
    [pack.basePriceCents, qty, tierLikes],
  );
  const moqIssues = validateOrderQty(qty, { minOrderQty: options.minOrderQty, maxOrderQty: options.maxOrderQty });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (moqIssues.length > 0) {
      setError(moqIssues[0]!.message);
      return;
    }
    if (options.requiresGst && !isValidGst(form.gstNumber)) {
      setError("A valid 15-character GSTIN is required for corporate orders.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submit({
        data: {
          packSlug: pack.slug,
          qty,
          companyName: form.companyName,
          contactName: form.contactName,
          contactEmail: form.contactEmail,
          ...(form.gstNumber ? { gstNumber: form.gstNumber.toUpperCase() } : {}),
          ...(form.poNumber ? { poNumber: form.poNumber } : {}),
          ...(form.contactPhone ? { contactPhone: form.contactPhone } : {}),
          ...(form.message ? { message: form.message } : {}),
        },
      });
      if (res.error || !res.quote) {
        setError(res.error ?? "We could not submit your request. Please try again.");
        return;
      }
      setDone(res.quote.quoteNumber);
    } catch {
      setError("We could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white text-black" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="relative overflow-hidden border-b border-black/10 bg-[#faf7f2]">
        <div className="absolute inset-0">
          <SafeImage
            src={pack.bannerImage ?? pack.heroImage}
            fallback={fallbackCollectionCover(pack.slug)}
            alt=""
            className="h-full w-full object-cover opacity-20"
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <nav className="text-[11px] uppercase tracking-[0.2em] text-black/45">
            <Link to="/corporate" className="hover:text-black">Corporate</Link>
            <span className="mx-2">/</span>
            <span className="text-black/70">{pack.name}</span>
          </nav>
          <h1 className="mt-4 max-w-2xl text-4xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{pack.name}</h1>
          {pack.shortDescription && <p className="mt-4 max-w-2xl text-black/65">{pack.shortDescription}</p>}
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-14 lg:grid-cols-[1.1fr_1fr] lg:px-10">
        <div>
          {pack.description && <p className="text-sm leading-relaxed text-black/70">{pack.description}</p>}

          <h2 className="mt-10 text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>Volume pricing</h2>
          <div className="mt-4 overflow-x-auto border border-black/10">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-black/10 text-[10px] uppercase tracking-[0.18em] text-black/50">
                <tr>
                  <th className="px-4 py-3">Quantity</th>
                  <th className="px-4 py-3">Unit price</th>
                  <th className="px-4 py-3">Tier</th>
                </tr>
              </thead>
              <tbody>
                {tiers.length === 0 && (
                  <tr><td className="px-4 py-4 text-black/55" colSpan={3}>Pricing is quoted per requirement. Submit a request to receive your rate.</td></tr>
                )}
                {tiers.map((t) => {
                  const unit = priceTiered(pack.basePriceCents, t.minQty, tierLikes).unitPriceCents;
                  return (
                    <tr key={t.id} className="border-b border-black/5 last:border-0">
                      <td className="px-4 py-3">{t.maxQty == null ? `${t.minQty}+` : `${t.minQty}–${t.maxQty}`} units</td>
                      <td className="px-4 py-3 font-medium">{formatPaise(unit)}</td>
                      <td className="px-4 py-3 text-black/55">{t.label ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <dl className="mt-8 grid gap-4 sm:grid-cols-2">
            <Fact label="Minimum order quantity" value={`${options.minOrderQty} units`} />
            <Fact label="Maximum order quantity" value={options.maxOrderQty ? `${options.maxOrderQty} units` : "No limit"} />
            <Fact label="Lead time" value={`${options.leadTimeDays} working days`} />
            <Fact label="Quote validity" value={`${options.quoteValidityDays} days`} />
            <Fact label="GST invoice" value={options.requiresGst ? "GSTIN required" : "Optional"} />
            <Fact label="Custom branding" value={options.allowBranding ? "Available" : "Not available on this pack"} />
          </dl>
          {options.allowBranding && options.brandingNote && (
            <p className="mt-4 border-l-2 border-[#C6A15B] pl-4 text-sm text-black/60">{options.brandingNote}</p>
          )}
          {(options.salesContactEmail || options.salesContactPhone) && (
            <p className="mt-8 text-sm text-black/60">
              Contact sales:{" "}
              {options.salesContactEmail && <a className="text-[#C6A15B]" href={`mailto:${options.salesContactEmail}`}>{options.salesContactEmail}</a>}
              {options.salesContactEmail && options.salesContactPhone && " · "}
              {options.salesContactPhone && <a className="text-[#C6A15B]" href={`tel:${options.salesContactPhone}`}>{options.salesContactPhone}</a>}
            </p>
          )}
        </div>

        <aside className="h-fit border border-black/12 bg-[#faf9f7] p-6 lg:sticky lg:top-24">
          {done ? (
            <div>
              <h2 className="text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>Request received</h2>
              <p className="mt-3 text-sm text-black/65">
                Your quote reference is <strong>{done}</strong>. Our corporate desk will respond with pricing shortly, and a
                confirmation has been sent to {form.contactEmail}.
              </p>
              <Link to="/account/quotes" className="mt-6 inline-block border border-black bg-black px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-white">
                Track my quotes
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>Request a quote</h2>

              <div>
                <label className={LABEL} htmlFor="qty">Quantity (units)</label>
                <input
                  id="qty"
                  type="number"
                  min={options.minOrderQty}
                  {...(options.maxOrderQty ? { max: options.maxOrderQty } : {})}
                  className={FIELD}
                  value={qty}
                  onChange={(e) => setQty(Number(e.target.value))}
                />
                {moqIssues.length > 0 && <p className="mt-1 text-xs text-red-700">{moqIssues[0]!.message}</p>}
              </div>

              <div className="border border-black/10 bg-white p-4 text-sm">
                <div className="flex justify-between"><span className="text-black/55">Indicative unit price</span><span className="font-medium">{formatPaise(priced.unitPriceCents)}</span></div>
                <div className="mt-1 flex justify-between"><span className="text-black/55">Volume discount</span><span className="font-medium">−{formatPaise(priced.discountCents)}</span></div>
                <div className="mt-2 flex justify-between border-t border-black/10 pt-2 text-base"><span>Estimated total</span><span className="font-semibold">{formatPaise(priced.totalCents)}</span></div>
                <p className="mt-2 text-[11px] text-black/45">Indicative only. Final pricing is confirmed on your quote.</p>
              </div>

              <div><label className={LABEL} htmlFor="company">Company name</label><input id="company" required className={FIELD} value={form.companyName} onChange={set("companyName")} maxLength={140} /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className={LABEL} htmlFor="gst">GSTIN{options.requiresGst ? "" : " (optional)"}</label><input id="gst" className={FIELD} value={form.gstNumber} onChange={set("gstNumber")} maxLength={15} placeholder="27AAAAA0000A1Z5" /></div>
                <div><label className={LABEL} htmlFor="po">PO number (optional)</label><input id="po" className={FIELD} value={form.poNumber} onChange={set("poNumber")} maxLength={60} /></div>
              </div>
              <div><label className={LABEL} htmlFor="cname">Contact name</label><input id="cname" required className={FIELD} value={form.contactName} onChange={set("contactName")} maxLength={120} /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className={LABEL} htmlFor="cemail">Contact email</label><input id="cemail" type="email" required className={FIELD} value={form.contactEmail} onChange={set("contactEmail")} maxLength={255} /></div>
                <div><label className={LABEL} htmlFor="cphone">Contact phone</label><input id="cphone" className={FIELD} value={form.contactPhone} onChange={set("contactPhone")} maxLength={20} /></div>
              </div>
              <div><label className={LABEL} htmlFor="msg">Requirement notes</label><textarea id="msg" rows={4} className={FIELD} value={form.message} onChange={set("message")} maxLength={1000} placeholder="Branding, delivery locations, target date…" /></div>

              {error && <p className="text-sm text-red-700">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full border border-black bg-black px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-black/85 disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Request quote"}
              </button>
              <p className="text-[11px] text-black/45">No payment is taken now. Our team confirms pricing before any order is raised.</p>
            </form>
          )}
        </aside>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-t border-black/10 pt-3">
      <dt className="text-[10px] uppercase tracking-[0.2em] text-black/45">{label}</dt>
      <dd className="mt-1 text-sm font-medium">{value}</dd>
    </div>
  );
}